import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileUp, Loader2, Upload } from 'lucide-react'

import {
  applyScriptImport,
  previewScriptImport,
  type ScriptImportEpisodeItem,
  type ScriptImportPreview,
} from '@/api/studio-production'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { formatGlobalEpisode } from '@/features/studio/script/season'
import { ProtoBadge, ProtoBtn, ProtoModal, ProtoTextarea } from '@/features/studio/components/ui-kit'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'

export function ScriptImportModal({
  open,
  onClose,
  productionId,
  scriptId,
  onApplied,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  scriptId: number
  onApplied: () => void
}) {
  const { t } = useTranslation()
  const fileRef = useRef<HTMLInputElement>(null)
  const [raw, setRaw] = useState('')
  const [extendTarget, setExtendTarget] = useState(true)
  const [preview, setPreview] = useState<ScriptImportPreview | null>(null)
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [previewing, setPreviewing] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setRaw('')
    setExtendTarget(true)
    setPreview(null)
    setSelected(new Set())
    setError(null)
  }, [open])

  const selectableItems = useMemo(
    () =>
      (preview?.items ?? []).filter(
        (item) => item.selected && item.skip_reason !== 'beyond_episode_target',
      ),
    [preview],
  )

  function syncSelectedFromPreview(data: ScriptImportPreview) {
    const next = new Set<number>()
    for (const item of data.items) {
      if (item.selected && item.skip_reason !== 'beyond_episode_target') {
        next.add(item.episode_no)
      }
    }
    setSelected(next)
  }

  async function handlePreview() {
    const body = raw.trim()
    if (!body) {
      setError(t('script.importEmpty'))
      return
    }
    setPreviewing(true)
    setError(null)
    try {
      const data = await previewScriptImport(productionId, scriptId, {
        body,
        extend_episode_target: extendTarget,
      })
      setPreview(data)
      syncSelectedFromPreview(data)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'script.importPreviewFailed'))
    } finally {
      setPreviewing(false)
    }
  }

  function toggleEpisode(episodeNo: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(episodeNo)) next.delete(episodeNo)
      else next.add(episodeNo)
      return next
    })
  }

  function toggleAll(checked: boolean) {
    if (!checked) {
      setSelected(new Set())
      return
    }
    setSelected(new Set(selectableItems.map((item) => item.episode_no)))
  }

  async function handleApply() {
    const body = raw.trim()
    if (!body || selected.size === 0) return
    setApplying(true)
    setError(null)
    try {
      await applyScriptImport(productionId, scriptId, {
        body,
        extend_episode_target: extendTarget,
        episode_nos: [...selected].sort((a, b) => a - b),
      })
      onApplied()
      onClose()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'script.importApplyFailed'))
    } finally {
      setApplying(false)
    }
  }

  async function handleFileChange(file: File | null) {
    if (!file) return
    try {
      const text = await file.text()
      setRaw(text)
      setPreview(null)
      setSelected(new Set())
      setError(null)
    } catch {
      setError(t('script.importReadFailed'))
    }
  }

  function rowBadge(item: ScriptImportEpisodeItem) {
    if (item.has_existing_body) {
      return (
        <ProtoBadge tone="amber" className="!text-[10px]">
          {t('script.importOverwrite')}
        </ProtoBadge>
      )
    }
    if (item.status === 'finalized') {
      return (
        <ProtoBadge tone="sky" className="!text-[10px]">
          {t('script.statusFinalized', { rev: 1 })}
        </ProtoBadge>
      )
    }
    return null
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('script.importTitle')}
      wide
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={previewing || applying}>
            {t('common.cancel')}
          </ProtoBtn>
          {preview ? (
            <ProtoBtn
              size="sm"
              disabled={applying || selected.size === 0}
              onClick={() => void handleApply()}
            >
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {t('script.importConfirm', { count: selected.size })}
            </ProtoBtn>
          ) : (
            <ProtoBtn size="sm" disabled={previewing || !raw.trim()} onClick={() => void handlePreview()}>
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
              {t('script.importPreview')}
            </ProtoBtn>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-zinc-500">{t('script.importHint')}</p>
        <div className="flex flex-wrap items-center gap-3">
          <ProtoBtn size="sm" variant="secondary" onClick={() => fileRef.current?.click()}>
            <FileUp className="h-3.5 w-3.5" />
            {t('script.importChooseFile')}
          </ProtoBtn>
          <div className="flex items-center gap-2">
            <Checkbox
              id="script-import-extend-target"
              checked={extendTarget}
              onCheckedChange={(checked) => {
                setExtendTarget(checked === true)
                setPreview(null)
              }}
            />
            <Label htmlFor="script-import-extend-target" className="text-xs font-normal text-muted-foreground">
              {t('script.importExtendTarget')}
            </Label>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".fountain,.fdx,.xml,.txt,.md,text/plain,application/xml,text/xml"
            className="hidden"
            onChange={(e) => void handleFileChange(e.target.files?.[0] ?? null)}
          />
        </div>
        <ProtoTextarea
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value)
            setPreview(null)
          }}
          rows={10}
          placeholder={t('script.importPlaceholder')}
        />
        <p className="text-[11px] text-zinc-600">{t('script.importMarkerHint')}</p>

        {error ? (
          <p className="rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </p>
        ) : null}

        {preview ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-500">
              <span>
                {t('script.importParsed', { count: preview.parsed_count })}
                {preview.will_extend_target
                  ? ` · ${t('script.importTargetExtend', {
                      from: preview.episode_target,
                      to: preview.new_episode_target,
                    })}`
                  : null}
              </span>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="script-import-select-all"
                  checked={selected.size === selectableItems.length && selectableItems.length > 0}
                  onCheckedChange={(checked) => toggleAll(checked === true)}
                />
                <Label htmlFor="script-import-select-all" className="text-xs font-normal text-muted-foreground">
                  {t('script.importSelectAll')}
                </Label>
              </div>
            </div>
            {preview.warnings?.length ? (
              <ul className="space-y-1 text-[11px] text-amber-400/90">
                {preview.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            ) : null}
            <div className="studio-scrollbar-hidden max-h-64 overflow-y-auto rounded-xl border border-white/[0.06]">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/[0.04] bg-[#12121a] text-[10px] uppercase tracking-wide text-zinc-500">
                    <th className="px-3 py-2" />
                    <th className="px-3 py-2">{t('script.colGlobalEpisode')}</th>
                    <th className="px-3 py-2">{t('script.colScenes')}</th>
                    <th className="px-3 py-2">{t('script.colStatus')}</th>
                    <th className="px-3 py-2">{t('script.importPreviewCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((item) => {
                    const disabled = item.skip_reason === 'beyond_episode_target'
                    return (
                      <tr
                        key={item.episode_no}
                        className="border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02]"
                      >
                        <td className="px-3 py-2">
                          <Checkbox
                            disabled={disabled}
                            checked={!disabled && selected.has(item.episode_no)}
                            onCheckedChange={() => toggleEpisode(item.episode_no)}
                            aria-label={formatGlobalEpisode(item.episode_no)}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-zinc-200">
                          {formatGlobalEpisode(item.episode_no)}
                        </td>
                        <td className="px-3 py-2 text-zinc-400">{item.scene_count}</td>
                        <td className="px-3 py-2">{rowBadge(item)}</td>
                        <td className="px-3 py-2 text-zinc-500">{item.body_preview || '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </ProtoModal>
  )
}
